/**
 * Dynamic Google Font loading utility.
 * Loads fonts on-demand and caches which fonts have been loaded.
 */

import { FONT_OPTIONS, FONT_STORAGE_KEY, DEFAULT_FONT_ID, getFontById, type FontOption } from '@/config/fonts'

// Track which Google Fonts have been loaded this session
const loadedFonts = new Set<string>()

/**
 * Load a Google Font by injecting a link element
 */
export function loadGoogleFont(font: FontOption): Promise<void> {
  return new Promise((resolve, reject) => {
    if (font.source !== 'google' || !font.googleFontUrl) {
      resolve()
      return
    }

    // Already loaded this session
    if (loadedFonts.has(font.id)) {
      resolve()
      return
    }

    // Check if link already exists in DOM
    const existingLink = document.querySelector(`link[data-font-id="${font.id}"]`)
    if (existingLink) {
      loadedFonts.add(font.id)
      resolve()
      return
    }

    // Create and inject link element
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = font.googleFontUrl
    link.setAttribute('data-font-id', font.id)

    link.onload = () => {
      loadedFonts.add(font.id)
      resolve()
    }

    link.onerror = () => {
      reject(new Error(`Failed to load font: ${font.name}`))
    }

    document.head.appendChild(link)
  })
}

/**
 * Apply a font globally to the app
 */
export function applyFontGlobally(fontFamily: string): void {
  // Base app font (consumed by body in index.css)
  document.documentElement.style.setProperty('--app-font-family', fontFamily)
  document.body.style.fontFamily = fontFamily

  // Backwards-compat: many components still reference --font-departure-mono
  // as the primary UI font. Point that var at the selected family so the
  // Google font selector applies across the app without rewriting components.
  document.documentElement.style.setProperty('--font-departure-mono', fontFamily)
}

/**
 * Get the currently selected font ID from localStorage
 */
export function getSelectedFontId(): string {
  if (typeof window === 'undefined') return DEFAULT_FONT_ID
  return localStorage.getItem(FONT_STORAGE_KEY) || DEFAULT_FONT_ID
}

/**
 * Save the selected font ID to localStorage
 */
export function saveSelectedFontId(fontId: string): void {
  if (typeof window === 'undefined') return
  if (fontId === DEFAULT_FONT_ID) {
    // Clear storage when resetting to default
    localStorage.removeItem(FONT_STORAGE_KEY)
  } else {
    localStorage.setItem(FONT_STORAGE_KEY, fontId)
  }
}

/**
 * Clear the font selection (reset to default)
 */
export function clearFontSelection(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(FONT_STORAGE_KEY)
}

/**
 * Load and apply a font by ID
 */
export async function loadAndApplyFont(fontId: string): Promise<void> {
  const font = getFontById(fontId)
  if (!font) {
    console.warn(`Font not found: ${fontId}`)
    return
  }

  // Load Google Font if needed
  if (font.source === 'google') {
    try {
      await loadGoogleFont(font)
    } catch (error) {
      console.error('Failed to load font:', error)
      return
    }
  }

  // Apply the font
  applyFontGlobally(font.family)
  saveSelectedFontId(fontId)

  // Dispatch event for any listeners
  window.dispatchEvent(new CustomEvent('kyogre-font-changed', { detail: { fontId, font } }))
}

/**
 * Reset font to default
 */
export function resetFontToDefault(): void {
  const defaultFont = getFontById(DEFAULT_FONT_ID)
  if (defaultFont) {
    applyFontGlobally(defaultFont.family)
  }
  clearFontSelection()
  window.dispatchEvent(new CustomEvent('kyogre-font-changed', { detail: { fontId: DEFAULT_FONT_ID } }))
}

/**
 * Initialize font on app load.
 * Call this early in the app lifecycle.
 */
export async function initializeFont(): Promise<void> {
  const savedFontId = getSelectedFontId()
  
  if (savedFontId && savedFontId !== DEFAULT_FONT_ID) {
    await loadAndApplyFont(savedFontId)
  } else {
    // Ensure default font is applied
    const defaultFont = getFontById(DEFAULT_FONT_ID)
    if (defaultFont) {
      applyFontGlobally(defaultFont.family)
    }
  }
}

